import React from 'react';

import { MultiCombobox, type MultiComboboxOption } from '@oysterjs/ui/multicombobox';
import { Select } from '@oysterjs/ui/select';

import { DocumentType } from '~/api/graphql';
import {
  type AcordFormFamily,
  STATE_FROM_POLICY,
  STATE_MULTI_STATE,
  US_STATE_NAMES,
  buildAcordPickerRows,
  decodeAcordSelection,
  encodeAcordSelection,
  familySearchKeywords,
  familyStateCodes,
  setFamilyState
} from './acord-form-families';

/**
 * The ACORD form picker.
 *
 * Two things distinguish it from the plain multi-select it replaces. It has a
 * search box, because the catalog is too long to scroll for a form you can name.
 * And it shows one row per form rather than one row per document: the forms
 * ACORD publishes separately for each state appear once, with a state selector
 * that appears underneath once the form is selected. Leaving that selector on
 * "determined from the policy" sends the form without a state, and Cara reads
 * the state off the policy while it works the request.
 */
export const AcordDocumentPicker = (props: {
  /** The flat catalog to collapse, already scoped to the caller's use case. */
  options: Array<{ documentType: DocumentType; title: string }>;
  value: DocumentType[];
  onChange: (value: DocumentType[]) => void;
  placeholder?: string;
  /** Caps how many forms can be selected at once. */
  maxSelections?: number;
  disabled?: boolean;
}) => {
  const rows = React.useMemo(() => buildAcordPickerRows(props.options), [props.options]);

  const comboboxOptions = React.useMemo<Array<MultiComboboxOption<DocumentType>>>(
    () =>
      rows.map((row) =>
        row.kind === 'family'
          ? {
              value: row.key,
              label: row.title,
              description: row.family.description,
              keywords: familySearchKeywords(row.family)
            }
          : { value: row.key, label: row.title }
      ),
    [rows]
  );

  const { keys, families } = React.useMemo(() => decodeAcordSelection(props.value), [props.value]);

  return (
    <div className="flex flex-col gap-3">
      <MultiCombobox
        value={keys}
        options={comboboxOptions}
        placeholder={props.placeholder}
        maxSelections={props.maxSelections}
        disabled={props.disabled}
        onChange={(next) => props.onChange(encodeAcordSelection(next, props.value))}
      />

      {families.map((selection) => (
        <FamilyStateField
          key={selection.family.formNumber}
          family={selection.family}
          state={selection.state}
          documentType={selection.documentType}
          disabled={props.disabled}
          onChange={(state) => props.onChange(setFamilyState(props.value, selection.family, state))}
        />
      ))}
    </div>
  );
};

/**
 * The state selector for one selected family. It names the document that will
 * be generated so the choice is not implicit: a user who picks Montana should
 * be able to see that they are getting ACORD 137 MT.
 */
const FamilyStateField = (props: {
  family: AcordFormFamily;
  state: string;
  documentType: DocumentType;
  disabled?: boolean;
  onChange: (state: string) => void;
}) => {
  const stateCodes = React.useMemo(() => familyStateCodes(props.family), [props.family]);
  const resolved = props.state === STATE_FROM_POLICY ? undefined : props.documentType;

  return (
    <div className="rounded-lg border border-neutral-950/10 bg-neutral-50 px-3 py-2.5 dark:border-white/10 dark:bg-white/5">
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="min-w-0">
          <p className="truncate text-sm/6 font-medium text-neutral-950 dark:text-white">
            {props.family.title}
          </p>
          <p className="text-xs/5 text-neutral-500 dark:text-neutral-400">
            {resolved
              ? `Generates ${resolved.replaceAll('_', ' ')}`
              : 'Cara picks the state edition from the policy'}
          </p>
        </div>
        <div className="sm:w-64 sm:shrink-0">
          <Select
            aria-label={`State for ${props.family.title}`}
            value={props.state}
            disabled={props.disabled}
            onChange={(event) => props.onChange(event.currentTarget.value)}
          >
            <option value={STATE_FROM_POLICY}>Determined from the policy</option>
            {props.family.multiState && (
              <option value={STATE_MULTI_STATE}>All states (multi-state edition)</option>
            )}
            {stateCodes.map((code) => (
              <option key={code} value={code}>
                {US_STATE_NAMES[code] ?? code}
              </option>
            ))}
          </Select>
        </div>
      </div>
    </div>
  );
};
